import { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getSimpleFlyDay } from "../../common/ApiUtils";
import { reverseDateToHuman } from "../../common/TimeUtils";
import { flyDayDispatch, activeDispatch } from "../../reduce/actions";
import { convertDateMinusToDot } from "../utils/flyDayUtils";

/**
 * Custom hook for managing FlyDay data fetching and state
 */
export const useFlyDayData = (dateParam, navigate) => {
  const dispatch = useDispatch();
  const [flyDay, setFlyDay] = useState(null);
  const [channelConnected, setChannelConnected] = useState(false);
  const active = useSelector(state => state?.active || {});
  const isMountedRef = useRef(true);
  const lastDateParamRef = useRef(dateParam);

  // Only update if dateParam actually changed
  useEffect(() => {
    if (lastDateParamRef.current !== dateParam) {
      lastDateParamRef.current = dateParam;
    }
  }, [dateParam]);

  // Use ref for active to avoid recreating callback
  const activeRef = useRef(active);
  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  const handleMessageReceived = useCallback((fd) => {
    if (!fd || !isMountedRef.current) {
      console.log("Error, not flyday or component unmounted");
      return;
    }

    const dayDate = convertDateMinusToDot(dateParam);
    if (fd.day === dayDate) {
      setFlyDay(fd);
      if (!activeRef.current.glider && fd.active) {
        dispatch(flyDayDispatch(fd));
        dispatch(activeDispatch(fd.active));
      } else {
        dispatch(flyDayDispatch(fd));
      }
    }
  }, [dateParam, dispatch]);

  const getFlyDay = useCallback(async () => {
    if (!isMountedRef.current) return;
    
    try {
      const response = await getSimpleFlyDay(dateParam);
      if (isMountedRef.current) {
        handleMessageReceived(response);
      }
    } catch (error) {
      console.error("Error fetching flyDay:", error);
    }
  }, [dateParam, handleMessageReceived]);

  // Store getFlyDay in a ref to avoid recreating visibility handler
  const getFlyDayRef = useRef(getFlyDay);
  useEffect(() => {
    getFlyDayRef.current = getFlyDay;
  }, [getFlyDay]);

  // Initialize on mount and when dateParam changes
  useEffect(() => {
    const date = reverseDateToHuman(dateParam);
    if (!date) {
      navigate('/');
      return;
    }

    setFlyDay(null);
    isMountedRef.current = true;
    getFlyDay();
    
    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateParam]); // Only depend on dateParam to avoid infinite loop

  // Handle visibility change - use ref to avoid recreating
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isMountedRef.current) {
        getFlyDayRef.current();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []); // Empty deps - use ref instead

  return {
    flyDay,
    setFlyDay,
    channelConnected,
    setChannelConnected,
    getFlyDay
  };
};

